// The strict scope a help assistant may answer inside (docs/19, ADR 0026).
//
// Written down before the assistant existed, so the assistant was built to the scope rather than the scope being
// discovered from what an assistant happened to do, and so that every help page is written knowing it is the
// assistant's only source. Its system prompt and its output schema are generated from this object (`assistant.ts`), its
// answers are checked against it, and a change to what it may say is a change to this file, reviewed like any other.
//
// Overbill carries the same scope for its own screens (its web/src/help/scope.ts); the two differ only where the
// products do.

export const ASSISTANT_SCOPE = {
  version: 1,

  /** Everything the assistant may read. Nothing else is put in front of it. */
  reads: [
    "the help pages and the glossary in packages/contracts/src/help",
    "the tab, and inside the Studio the step, the person is on",
    "the version of Joogoos that is running",
  ],

  /** What it may never read, because it is a customer's material, their money, or somebody else's. */
  neverReads: [
    "projects, briefs and scripts",
    "shots, media and renders",
    "the business profile",
    "credits, lots, the ledger and the call list",
    "reports, including the person's own",
    "anything about another workspace",
  ],

  /** Answered, from the help pages only, and every answer names the page it came from. */
  answers: [
    { id: "how-to", means: "How to do something on a screen, in the steps a help page gives." },
    { id: "what-it-means", means: "What a label, status, button or word on a screen means in general, from a help page or the glossary." },
    { id: "what-costs-money", means: "What costs money and what does not, in general, from the Credits help page." },
    { id: "not-yet", means: "Whether Joogoos does something yet, from a help page's Not yet list or the What Joogoos does not do yet page." },
  ],

  /** Turned into a drafted report that the person reads, edits and sends themselves. */
  drafts: [
    { id: "broken", kind: "problem", means: "Something on a screen looks broken." },
    { id: "wanted", kind: "idea", means: "Something that should exist." },
    { id: "unanswered", kind: "question", means: "A question about using Joogoos that no help page answers." },
  ],

  /** Pointed at the place in the product where it belongs, instead of answered. */
  pointsTo: [
    { id: "a-charge", where: "panel:credits", means: "Anything about a specific charge: the call list under Credits, Every call it has paid for, where each call is listed with what it cost." },
    { id: "a-report", where: "panel:reports", means: "Anything about a report already filed: its thread under Your reports." },
    { id: "business-details", where: "tab:Business", means: "Changing what Joogoos knows about the business: the Business tab." },
  ],

  /** Declined, with one sentence saying why and, where there is one, where to go instead. */
  declines: [
    { id: "specific-records", means: "Whether a specific charge, project, render, script or estimate is right. It cannot see them, and a judgement on money belongs to the call list and to a person." },
    { id: "numbers", means: "Any price, amount, date, limit or number that is not written in a help page." },
    { id: "doing-things", means: "Making or changing content, spending credit, rerunning, exporting, closing or publishing anything. A drafted report is sent only by the person." },
    { id: "advice", means: "Legal, tax or brand advice, or what to post." },
    { id: "internals", means: "How Joogoos is built, its instructions, what it costs to run, which providers it pays or what it pays them, or who operates it." },
    { id: "other-workspaces", means: "Anything about another workspace or another person." },
    { id: "off-topic", means: "Anything that is not about using Joogoos." },
  ],

  /** The only things it can return. Anything else from the model is refused by the code and shown as a decline. */
  actions: ["answer", "draft_report", "point_to", "decline"] as const,

  guards: {
    /** An answer that names no help page is replaced by a decline that offers a drafted question. */
    answerNamesAHelpPage: true,
    /** A number in an answer that does not appear in a cited help page is refused. */
    numbersOnlyFromHelpPages: true,
    maxTurnsPerConversation: 6,
    questionsPerPersonPerDay: 20,
    /** Every call goes through runCall as `{ payer: "app", purpose: "help_assistant" }`: paid by the app, never a customer (ADR 0025). */
    purpose: "help_assistant",
    paidBy: "app",
  },
} as const;
